import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the provider from URL params (google, github, etc.)
        const provider = searchParams.get('provider');

        // Get the session from the URL hash
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Error getting session:', sessionError);
          setError('Failed to complete sign in. Please try again.');
          setTimeout(() => navigate('/login?error=oauth_failed'), 2000);
          return;
        }

        if (!session) {
          setError('No session found. Please try signing in again.');
          setTimeout(() => navigate('/login?error=no_session'), 2000);
          return;
        }

        // Sync OAuth user with backend (creates/updates user profile)
        try {
          await axios.post(
            `${API_URL}/auth/oauth-sync`,
            { provider },
            {
              headers: {
                Authorization: `Bearer ${session.access_token}`
              }
            }
          );

          // Get updated user profile
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          });

          const userData = response.data;
          setSyncing(false);

          // Redirect based on user role
          setTimeout(() => {
            if (userData.role === 'coach') {
              navigate('/coach', { replace: true });
            } else {
              navigate('/client', { replace: true });
            }
          }, 500);
        } catch (syncError) {
          console.error('Error syncing OAuth user:', syncError);

          // Check if this is an account conflict (user exists with different auth method)
          if (syncError.response?.status === 409) {
            setError('An account with this email already exists. Please sign in with your password.');
            setTimeout(() => navigate('/login?error=account_exists'), 3000);
            return;
          }

          // For other errors, try to redirect anyway
          setSyncing(false);
          console.warn('Failed to sync user profile, attempting redirect anyway');

          setTimeout(() => {
            if (user?.role === 'coach') {
              navigate('/coach', { replace: true });
            } else {
              navigate('/client', { replace: true });
            }
          }, 1000);
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setError('An error occurred during sign in. Please try again.');
        setTimeout(() => navigate('/login?error=oauth_failed'), 2000);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams, user]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1816]">
      <div className="text-center max-w-md px-4">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-400 text-lg font-semibold mb-2">Sign In Failed</p>
            <p className="text-gray-400 text-sm">{error}</p>
            <p className="text-gray-500 text-xs mt-4">Redirecting to login page...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3a352f] border-t-[#C59D52] mx-auto mb-4"></div>
            <p className="text-white text-lg font-semibold mb-2">
              {syncing ? 'Setting up your account...' : 'Completing sign in...'}
            </p>
            <p className="text-gray-400 text-sm">Please wait a moment</p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;

